import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MapPin, Phone, Mail, Building2, FileCheck2, ExternalLink, Sparkles,
  Heart, Award, Truck, Leaf, GraduationCap, Shield, Star, Zap,
} from "lucide-react";
import { useEnquiry, formatEnquiry } from "@/hooks/useEnquiry";
import { ProductPicker } from "@/components/ProductPicker";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useContent, t, parseHiddenSet } from "@/hooks/useContent";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const ICON_MAP = {
  mappin: MapPin, phone: Phone, mail: Mail, building: Building2, filecheck: FileCheck2,
  external: ExternalLink, sparkles: Sparkles, heart: Heart, award: Award,
  truck: Truck, leaf: Leaf, graduationcap: GraduationCap, shield: Shield, star: Star, zap: Zap,
};

const TONES = {
  red: { icon: "bg-[#D9381E] text-white", card: "bg-white border-black/10" },
  gold: { icon: "bg-[#F2A900] text-[#111]", card: "bg-white border-black/10" },
  dark: { icon: "bg-[#111] text-[#F2A900]", card: "bg-white border-black/10" },
  cream: { icon: "text-[#D9381E]", card: "bg-[#F3EAD3] border-black/10" },
};

const DEFAULT_CARDS = [
  { icon: "mappin", eyebrow: "Corporate Address", value: "", href: "", cta: "", tone: "red" },
];

const DEFAULT_FIELDS = [
  { key: "name", label: "Name", type: "text", required: true, colspan: 1, placeholder: "", options: "" },
  { key: "email", label: "Email", type: "email", required: true, colspan: 1, placeholder: "", options: "" },
  { key: "phone", label: "Phone (optional)", type: "phone", required: false, colspan: 2, placeholder: "", options: "" },
  { key: "message", label: "Message", type: "textarea", required: true, colspan: 2, placeholder: "", options: "" },
];

function parseCards(raw) {
  if (!raw) return DEFAULT_CARDS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_CARDS;
    return arr.map((c) => ({
      icon: c?.icon || "sparkles",
      eyebrow: c?.eyebrow || "",
      value: c?.value || "",
      href: c?.href || "",
      cta: c?.cta || "",
      tone: c?.tone || "red",
      hidden: !!c?.hidden,
    }));
  } catch { return DEFAULT_CARDS; }
}

function parseFields(raw) {
  if (!raw) return DEFAULT_FIELDS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_FIELDS;
    return arr.map((f, i) => ({
      key: (f?.key || `field_${i}`).toString(),
      label: f?.label || "",
      type: f?.type || "text",
      required: !!f?.required,
      colspan: Number(f?.colspan) === 2 ? 2 : 1,
      placeholder: f?.placeholder || "",
      options: f?.options || "",
      hidden: !!f?.hidden,
    }));
  } catch { return DEFAULT_FIELDS; }
}

function IconTile({ name, tone }) {
  const Icon = ICON_MAP[String(name || "").toLowerCase()] || Sparkles;
  if (tone === "cream") {
    return <Icon className="text-[#D9381E]" />;
  }
  const cls = TONES[tone]?.icon || TONES.red.icon;
  return (
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${cls}`}>
      <Icon />
    </div>
  );
}

function ContactCard({ card, testid }) {
  const isLarge = card.tone !== "cream";
  const cardClass = TONES[card.tone]?.card || TONES.red.card;
  const inner = (
    <div className={isLarge ? "flex items-start gap-4" : ""}>
      <IconTile name={card.icon} tone={card.tone} />
      <div className={isLarge ? "" : "mt-2"}>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B7280]">{card.eyebrow}</div>
        <div className={`mt-1 font-display font-bold text-[#111] ${isLarge ? "text-lg leading-tight" : ""}`}>
          {card.value}
        </div>
        {card.cta && <div className="mt-2 text-xs text-[#D9381E] font-bold">{card.cta}</div>}
      </div>
    </div>
  );

  const commonClass = `${isLarge ? "block p-6 rounded-3xl" : "p-5 rounded-2xl"} border ${cardClass} hover:border-[#D9381E] transition-colors`;

  if (card.href) {
    const isExternal = /^https?:\/\//i.test(card.href) || card.href.startsWith("mailto:") || card.href.startsWith("tel:");
    return (
      <a
        href={card.href}
        target={isExternal && !card.href.startsWith("mailto:") && !card.href.startsWith("tel:") ? "_blank" : undefined}
        rel="noreferrer"
        data-testid={testid}
        className={commonClass}
      >
        {inner}
      </a>
    );
  }
  return <div data-testid={testid} className={commonClass}>{inner}</div>;
}

function DynamicField({ field, value, onChange }) {
  const label = (
    <Label className="text-white/80 text-xs uppercase tracking-widest" data-testid={`contact-label-${field.key}`}>
      {field.label}{field.required ? " *" : ""}
    </Label>
  );
  const cls = "mt-1 bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#F2A900]";
  const common = {
    required: field.required,
    "data-testid": `contact-${field.key}`,
    value: value ?? "",
    placeholder: field.placeholder || "",
    onChange: (e) => onChange(e.target.value),
    className: cls,
  };
  if (field.type === "textarea") return (<>{label}<Textarea rows={5} {...common} /></>);
  if (field.type === "select") {
    const opts = String(field.options || "").split("|").map((o) => o.trim()).filter(Boolean);
    return (
      <>
        {label}
        <select
          required={field.required}
          data-testid={`contact-${field.key}`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 h-10 w-full rounded-md bg-white/5 border border-white/20 text-white px-3"
        >
          <option value="" className="text-[#111]">Select…</option>
          {opts.map((o) => (<option key={o} value={o} className="text-[#111]">{o}</option>))}
        </select>
      </>
    );
  }
  if (field.type === "checkbox") {
    return (
      <div className="flex items-center gap-2 mt-2">
        <Checkbox
          id={`contact-${field.key}`}
          data-testid={`contact-${field.key}`}
          checked={!!value}
          onCheckedChange={(v) => onChange(!!v)}
          className="border-white/30 data-[state=checked]:bg-[#F2A900] data-[state=checked]:text-[#111]"
        />
        <Label htmlFor={`contact-${field.key}`} className="text-white/80 text-sm">
          {field.label}{field.required ? " *" : ""}
        </Label>
      </div>
    );
  }
  const htmlType = field.type === "phone" ? "tel" : field.type === "number" ? "number" : field.type === "email" ? "email" : "text";
  return (<>{label}<Input type={htmlType} {...common} /></>);
}

export default function Contact() {
  const c = useContent();
  const hidden = parseHiddenSet(c);
  const H = (id) => hidden.has(id);
  const cards = useMemo(() => parseCards(t(c, "contact.cards", "")).filter((cd) => !cd.hidden), [c]);
  const fields = useMemo(() => parseFields(t(c, "contact.form_fields", "")).filter((f) => !f.hidden), [c]);

  const initial = useMemo(() => {
    const o = {};
    fields.forEach((f) => { o[f.key] = f.type === "checkbox" ? false : ""; });
    return o;
  }, [fields]);

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  // reset shape if fields change dynamically
  useMemo(() => setForm((prev) => ({ ...initial, ...prev })), [initial]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const enquiry = useEnquiry();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const visibleProducts = products.filter((p) => !H(`product:${p.id}`));
  const showPicker = !H("contact.form.products") && visibleProducts.length > 0;

  const [params, setParams] = useSearchParams();
  const formRef = useRef(null);
  const arrived = useRef(false);
  useEffect(() => {
    const name = params.get("product");
    if (arrived.current || !(name || params.get("enquiry")) || !products.length || !fields.length) { return; }
    arrived.current = true;
    const p = name && products.find((x) => x.name === name);
    if (p) enquiry.add(p);
    const msgField = fields.find((f) => f.type === "textarea") || fields.find((f) => f.key === "message");
    if (msgField) setForm((prev) => (prev[msgField.key] ? prev : { ...prev, [msgField.key]: "Hi! I'd like to know more about availability and pricing for the products I've selected." }));
    params.delete("product");
    params.delete("enquiry");
    setParams(params, { replace: true });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [products, fields, params]);

  const validate = () => {
    for (const f of fields) {
      const v = form[f.key];
      if (f.required) {
        if (f.type === "checkbox" && !v) return `Please tick ${f.label}`;
        if (f.type !== "checkbox" && (v === "" || v == null)) return `${f.label} is required`;
      }
      if (v && f.type === "email" && !/^\S+@\S+\.\S+$/.test(v)) return `${f.label} must be a valid email`;
      if (v && f.type === "phone" && !/^[+\d\s\-()]{6,}$/.test(v)) return `${f.label} must be a valid phone`;
      if (v && f.type === "number" && Number.isNaN(Number(v))) return `${f.label} must be a number`;
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const picked = showPicker ? enquiry.items.filter((i) => visibleProducts.some((p) => p.id === i.id)) : [];
      await api.post("/contact", { fields: picked.length ? { ...form, products: formatEnquiry(picked) } : form });
      enquiry.clear();
      toast.success("Thanks! We'll get back to you soon.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <Seo
        path="/contact"
        title="Contact B-Raw — Reach the Founder in Sirsa, Haryana"
        description="Get in touch with B-Raw for distribution queries, bulk orders and feedback. Reach founder Devender Yadav at the Sirsa workshop — hand-crafted, FSSAI-certified snacks made with real ingredients."
      />
      <div className="max-w-3xl">
        {!H("contact.eyebrow") && (
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
            {t(c, "contact.eyebrow", "Reach the founder")}
          </div>
        )}
        {!H("contact.title") && (
          <h1 className="mt-2 font-display font-black text-5xl sm:text-6xl tracking-tighter text-[#111]">
            {t(c, "contact.title", "Say hi. We read every note.")}
          </h1>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {!H("contact.cards.section") && (
          <div className="lg:col-span-5 min-w-0 space-y-4 [overflow-wrap:anywhere]" data-testid="contact-cards">
            {cards.filter((c) => c.tone !== "cream").map((card, i) => (
              <ContactCard key={i} card={card} testid={`contact-card-${i}`} />
            ))}
            {cards.some((c) => c.tone === "cream") && (
              <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-4" data-testid="contact-cream-grid">
                {cards.filter((c) => c.tone === "cream").map((card, i) => (
                  <ContactCard key={`cream-${i}`} card={card} testid={`contact-card-cream-${i}`} />
                ))}
              </div>
            )}
          </div>
        )}

        {!H("contact.form.section") && (
        <form
          ref={formRef}
          onSubmit={submit}
          className={`${H("contact.cards.section") ? "lg:col-span-12" : "lg:col-span-7"} scroll-mt-24 p-6 sm:p-10 rounded-3xl bg-[#111] text-white grain-overlay relative overflow-hidden`}
          data-testid="contact-form"
        >
          <h2 className="font-display font-black text-3xl" data-testid="contact-form-title">
            {t(c, "contact.form_title", "Drop us a message")}
          </h2>
          <p className="mt-2 text-white/70 text-sm" data-testid="contact-form-subtitle">
            {t(c, "contact.form_subtitle", "Have a distribution query, a bulk order or feedback on a flavour? We love mail.")}
          </p>
          {showPicker && <ProductPicker products={visibleProducts} enquiry={enquiry} />}

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.colspan === 2 ? "sm:col-span-2" : ""}>
                <DynamicField field={f} value={form[f.key]} onChange={(v) => setField(f.key, v)} />
              </div>
            ))}
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="mt-6 bg-[#D9381E] hover:bg-[#B02C15] text-white rounded-full px-6 py-6 font-bold"
          >
            {loading ? "Sending…" : t(c, "contact.form_button", "Send message")}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
